/**
 * Bank Statements API Service
 *
 * Handles bank statement upload, preview, and list operations via Platform API.
 * File uploads use raw fetch (multipart); all other endpoints use typed platformApi client.
 */

import { getAccessToken } from '@/lib/auth';
import { platformApi } from '@/lib/platform-api';
import { getPlatformApiUrl } from '@/lib/platform-api-url';
import type {
  BankColumnMapping,
  BankColumnMappingInput,
  BankStatementClassificationAuditEvent,
  BankStatementFilters,
  BankStatementLinesResponse,
  BankStatementPreviewResponse,
  BankStatementSummaryResponse,
  BankStatementUploadResponse,
  ColumnMappingsResponse,
  CsvPreviewResponse,
} from '../types/bankStatements';

type PlatformMutationResult = { data?: unknown; error?: unknown };
type PlatformMutation = (path: string, options?: unknown) => Promise<PlatformMutationResult>;

const platformPost = platformApi.POST as unknown as PlatformMutation;
const platformPatch = platformApi.PATCH as unknown as PlatformMutation;

function columnMappingCreateBody(input: BankColumnMappingInput) {
  return {
    ...input,
    direction_column: input.direction_column ?? null,
    counterparty_column: input.counterparty_column ?? null,
    reference_column: input.reference_column ?? null,
    description_column: input.description_column ?? null,
    date_format: input.date_format ?? 'YYYY-MM-DD',
    amount_decimal_separator: input.amount_decimal_separator ?? '.',
    direction_credit_value: input.direction_credit_value ?? 'CR',
    is_default: input.is_default ?? false,
  };
}

// ---------------------------------------------------------------------------
// List & Summary
// ---------------------------------------------------------------------------

interface FetchBankStatementsOptions {
  filters?: BankStatementFilters;
}

export async function fetchBankStatementLines(options: FetchBankStatementsOptions = {}): Promise<BankStatementLinesResponse> {
  const { filters = {} } = options;

  const query: Record<string, unknown> = {};
  if (filters.limit) query.limit = filters.limit;
  if (filters.cursor) query.cursor = filters.cursor;
  if (filters.sort) query.sort = filters.sort;

  if (filters.status && filters.status !== 'all') {
    query.status = filters.status;
  } else if (filters.statusNotIn && filters.statusNotIn.length > 0) {
    query.status_not_in = filters.statusNotIn.join(',');
  }

  if (filters.classificationTypeIn && filters.classificationTypeIn.length > 0) {
    query.classification_type_in = filters.classificationTypeIn.join(',');
  } else if (filters.classificationType && filters.classificationType !== 'all') {
    query.classification_type = filters.classificationType;
  }
  if (filters.classificationTypeNotIn && filters.classificationTypeNotIn.length > 0) {
    query.classification_type_not_in = filters.classificationTypeNotIn.join(',');
  }

  if (filters.dateFrom) query.date_from = filters.dateFrom;
  if (filters.dateTo) query.date_to = filters.dateTo;
  if (filters.direction && filters.direction !== 'all') query.direction = filters.direction;
  if (filters.source && filters.source !== 'all') query.source = filters.source;
  if (filters.search?.trim()) query.search = filters.search.trim();

  const { data, error } = await platformApi.GET('/platform/v1/finance/bank-statements', {
    params: { query },
  });

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to fetch bank statements';
    throw new Error(String(msg));
  }

  const envelope = data as { data: Record<string, unknown>[]; has_more: boolean; next_cursor?: string | null };
  return {
    data: envelope.data.map((row) => ({
      ...row,
      amount_millicents: typeof row.amount_in_cents === 'number' ? row.amount_in_cents * 1000 : undefined,
      currency: typeof row.currency === 'string' ? row.currency : 'EUR',
    })) as BankStatementLinesResponse['data'],
    has_more: envelope.has_more,
    next_cursor: envelope.next_cursor ?? null,
  };
}

export interface BankStatementSummaryFilters {
  direction?: 'credit' | 'debit';
  source?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export async function fetchBankStatementSummary(
  options: { filters?: BankStatementSummaryFilters } = {}
): Promise<BankStatementSummaryResponse> {
  const { filters = {} } = options;
  const query: Record<string, unknown> = {};
  if (filters.direction) query.direction = filters.direction;
  if (filters.source) query.source = filters.source;
  if (filters.dateFrom) query.date_from = filters.dateFrom;
  if (filters.dateTo) query.date_to = filters.dateTo;
  if (filters.search?.trim()) query.search = filters.search.trim();

  const { data, error } = await platformApi.GET('/platform/v1/finance/bank-statements/summary', {
    params: { query },
  });

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to fetch summary';
    throw new Error(String(msg));
  }

  const envelope = data as { object: string; data: BankStatementSummaryResponse; request_id: string };
  return envelope.data;
}

export interface BankStatementUploadStatus {
  count: number;
  line_ids: string[];
  processed: number;
}

// The generated Platform API schema on this branch does not include the
// upload-status route yet; CI regenerates types from the live OpenAPI spec.
const UPLOAD_STATUS_PATH = '/platform/v1/finance/bank-statements/upload-status' as never;

/**
 * Lines from a single upload batch still missing a TigerBeetle import transfer.
 */
export async function fetchBankStatementUploadStatus(fileUploadId: string): Promise<BankStatementUploadStatus> {
  const { data, error } = await platformApi.GET(UPLOAD_STATUS_PATH, {
    params: { query: { file_upload_id: fileUploadId } },
  } as never);

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to fetch upload status';
    throw new Error(String(msg));
  }

  const envelope = data as { count?: number; line_ids?: string[]; processed?: number };
  return {
    count: envelope.count ?? 0,
    line_ids: envelope.line_ids ?? [],
    processed: envelope.processed ?? 0,
  };
}

// ---------------------------------------------------------------------------
// File Upload Helpers (raw fetch — multipart not supported by openapi-fetch)
// ---------------------------------------------------------------------------

async function multipartFetch<T>(path: string, formData: FormData): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${getPlatformApiUrl()}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Request-ID': crypto.randomUUID(),
    },
    body: formData,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Request failed with status ${response.status}`);
  }

  return response.json() as Promise<T>;
}

export async function previewBankStatement(
  file: File,
  fileType: 'mt940' | 'csv',
  columnMappingId?: string
): Promise<BankStatementPreviewResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('file_type', fileType);
  if (columnMappingId) formData.append('column_mapping_id', columnMappingId);

  return multipartFetch<BankStatementPreviewResponse>('/platform/v1/finance/bank-statements/preview', formData);
}

export async function uploadBankStatement(
  file: File,
  fileType: 'mt940' | 'csv',
  columnMappingId?: string
): Promise<BankStatementUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('file_type', fileType);
  if (columnMappingId) formData.append('column_mapping_id', columnMappingId);

  return multipartFetch<BankStatementUploadResponse>('/platform/v1/finance/bank-statements/upload', formData);
}

export async function previewCsvHeaders(file: File): Promise<CsvPreviewResponse> {
  const formData = new FormData();
  formData.append('file', file);

  return multipartFetch<CsvPreviewResponse>('/platform/v1/finance/bank-statements/csv-preview', formData);
}

// ---------------------------------------------------------------------------
// Column Mappings
// ---------------------------------------------------------------------------

export async function fetchColumnMappings(): Promise<ColumnMappingsResponse> {
  const { data, error } = await platformApi.GET('/platform/v1/finance/bank-statements/column-mappings', {});

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to fetch column mappings';
    throw new Error(String(msg));
  }

  const envelope = data as { data?: BankColumnMapping[]; mappings?: BankColumnMapping[] };
  return { mappings: envelope.data ?? envelope.mappings ?? [] };
}

export async function createColumnMapping(input: BankColumnMappingInput): Promise<BankColumnMapping> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/bank-statements/column-mappings', {
    body: columnMappingCreateBody(input),
  });

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to create column mapping';
    throw new Error(String(msg));
  }

  return data as unknown as BankColumnMapping;
}

export async function updateColumnMapping(mappingId: string, input: Partial<BankColumnMappingInput>): Promise<BankColumnMapping> {
  const { data, error } = await platformPatch('/platform/v1/finance/bank-statements/column-mappings/{mapping_id}', {
    params: { path: { mapping_id: mappingId } },
    body: input,
  });

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to update column mapping';
    throw new Error(String(msg));
  }

  return data as unknown as BankColumnMapping;
}

export async function deleteColumnMapping(mappingId: string): Promise<void> {
  const { error } = await platformApi.DELETE('/platform/v1/finance/bank-statements/column-mappings/{mapping_id}', {
    params: { path: { mapping_id: mappingId } },
  });

  if (error) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to delete column mapping';
    throw new Error(String(msg));
  }
}

// ---------------------------------------------------------------------------
// Reclassify & Manual Match
// ---------------------------------------------------------------------------

export interface ReclassifyResponse {
  line_id: string;
  status: string;
  message: string;
}

export interface ManualMatchResponse {
  line_id: string;
  status: string;
  message: string;
}

export async function reclassifyBankStatementLine(lineId: string, classificationType: string): Promise<ReclassifyResponse> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/bank-statements/{line_id}/actions/reclassify', {
    params: { path: { line_id: lineId } },
    body: { classification_type: classificationType },
  });

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to reclassify line';
    throw new Error(String(msg));
  }

  return data as unknown as ReclassifyResponse;
}

export interface MarkReconciledResponse {
  line_id: string;
  status: string;
}

export async function markLineReconciled(lineId: string, notes?: string): Promise<MarkReconciledResponse> {
  const { data, error } = await platformPost('/platform/v1/finance/bank-statements/{line_id}/actions/mark-reconciled', {
    params: { path: { line_id: lineId } },
    body: notes ? { notes } : undefined,
  });

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to mark as reconciled';
    throw new Error(String(msg));
  }

  return data as unknown as MarkReconciledResponse;
}

export async function manualMatchBankStatementLine(lineId: string, caseId: string, notes?: string): Promise<ManualMatchResponse> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/bank-statements/{line_id}/actions/match', {
    params: { path: { line_id: lineId } },
    body: { case_id: caseId, notes },
  });

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to match line';
    throw new Error(String(msg));
  }

  return data as unknown as ManualMatchResponse;
}

export async function fetchBankStatementClassificationAudit(lineId: string): Promise<BankStatementClassificationAuditEvent[]> {
  const { data, error } = await platformApi.GET('/platform/v1/finance/bank-statements/{line_id}/classification-audit' as any, {
    params: { path: { line_id: lineId } },
  });

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to fetch classification audit';
    throw new Error(String(msg));
  }

  const envelope = data as { data: BankStatementClassificationAuditEvent[] };
  return envelope.data ?? [];
}

// ---------------------------------------------------------------------------
// PSP Settlement Suggestions & Confirmation
// ---------------------------------------------------------------------------

export interface PspSettlementEntry {
  settlement_id: string;
  transaction_id: string;
  entry_type: 'payment' | 'refund' | 'fee';
  amount_in_cents: number;
  case_id: string | null;
  event_timestamp: string | null;
  correction_action?: string;
}

export interface PspSettlementSuggestionsResponse {
  line_id: string;
  psp_type: string;
  reconciliation_status: string;
  entries: PspSettlementEntry[];
  suggested_ids: string[] | null;
  batch_suggestion?: {
    source: 'multibanco_batch_id' | 'auto_match' | 'manual';
    file_batch_id: string;
    bank_statement_line_id: string;
    matched_amount_in_cents: number;
    transaction_count: number;
    settlement_ids: string[];
    status: 'exact' | 'mismatch' | 'missing_settlements';
  };
}

export interface ConfirmPspReceiptResponse {
  line_id: string;
  status: string;
  message: string;
  matched_count?: number;
  bank_receipts_created?: number;
}

export interface PspTypesResponse {
  psp_types: string[];
}

export interface PspReceiptLine {
  id: string;
  transaction_date: string;
  counterparty_name: string | null;
  description: string | null;
  direction: string;
  classification_type: string | null;
  reconciliation_status: string;
  amount_millicents: number | null;
  currency: string;
  detected_psp_type: string | null;
  has_auto_suggestion: boolean;
  /** Auto-matcher output; used to pre-select rows when the suggestions API omits suggested_ids */
  suggested_settlement_ids?: string[] | null;
}

export interface PspReceiptLinesResponse {
  data: PspReceiptLine[];
  has_more: boolean;
  next_cursor: string | null;
}

export interface FetchPspSuggestionsParams {
  fromDate?: string;
  toDate?: string;
  pspType?: string;
  paymentMethod?: string;
}

export async function fetchAvailablePspTypes(): Promise<PspTypesResponse> {
  const { data, error } = await platformApi.GET('/platform/v1/finance/bank-statements/psp-types', {});

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to fetch PSP types';
    throw new Error(String(msg));
  }

  const envelope = data as { data?: string[]; psp_types?: string[] };
  return { psp_types: envelope.data ?? envelope.psp_types ?? [] };
}

export async function fetchPspReceiptLines(cursor?: string, limit = 50): Promise<PspReceiptLinesResponse> {
  const query: Record<string, unknown> = { limit };
  if (cursor) query.cursor = cursor;

  const { data, error } = await platformApi.GET('/platform/v1/finance/bank-statements/psp-receipt-lines', {
    params: { query },
  });

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to fetch PSP receipt lines';
    throw new Error(String(msg));
  }

  const envelope = data as { data: Record<string, unknown>[]; has_more: boolean; next_cursor?: string | null };
  return {
    data: envelope.data.map((row) => ({
      ...row,
      amount_millicents: typeof row.amount_in_cents === 'number' ? row.amount_in_cents * 1000 : null,
      currency: typeof row.currency === 'string' ? row.currency : 'EUR',
    })) as PspReceiptLinesResponse['data'],
    has_more: envelope.has_more,
    next_cursor: envelope.next_cursor ?? null,
  };
}

export async function fetchPspSettlementSuggestions(
  lineId: string,
  params?: FetchPspSuggestionsParams
): Promise<PspSettlementSuggestionsResponse> {
  const query: Record<string, unknown> = {};
  if (params?.fromDate) query.from_date = params.fromDate;
  if (params?.toDate) query.to_date = params.toDate;
  if (params?.pspType) query.psp_type = params.pspType;
  if (params?.paymentMethod) query.payment_method = params.paymentMethod;

  const { data, error } = await platformApi.GET('/platform/v1/finance/bank-statements/{line_id}/psp-settlement-suggestions', {
    params: { path: { line_id: lineId }, query },
  });

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to fetch PSP suggestions';
    throw new Error(String(msg));
  }

  return data as unknown as PspSettlementSuggestionsResponse;
}

export async function confirmPspReceipt(lineId: string, settlementIds?: string[]): Promise<ConfirmPspReceiptResponse> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/bank-statements/{line_id}/actions/confirm-psp-receipt', {
    params: { path: { line_id: lineId } },
    body: settlementIds ? { settlement_ids: settlementIds } : {},
  });

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to confirm PSP receipt';
    throw new Error(String(msg));
  }

  return data as unknown as ConfirmPspReceiptResponse;
}

// ---------------------------------------------------------------------------
// Atlar Sync
// ---------------------------------------------------------------------------

export interface AtlarSyncResponse {
  status: string;
  message: string;
}

export async function triggerAtlarSync(): Promise<AtlarSyncResponse> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/bank-statements/actions/sync-atlar', {});

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to trigger Atlar sync';
    throw new Error(String(msg));
  }

  return data as unknown as AtlarSyncResponse;
}

// ---------------------------------------------------------------------------
// Case Search
// ---------------------------------------------------------------------------

export interface CaseSearchResult {
  id: string;
  customer_name: string | null;
  customer_iban: string | null;
  outstanding_balance: number | null;
  journey_status: string;
  case_created_at: string | null;
  last_payment_date: string | null;
  last_payment_amount: number | null;
  match_confidence: number;
  match_reason: string;
}

export interface CaseSearchResponse {
  results: CaseSearchResult[];
  query: string;
}

export async function searchCasesForLine(lineId: string, query: string): Promise<CaseSearchResponse> {
  const { data, error } = await platformApi.GET('/platform/v1/finance/bank-statements/{line_id}/search-cases', {
    params: { path: { line_id: lineId }, query: { query } },
  });

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to search cases';
    throw new Error(String(msg));
  }

  const envelope = data as { data?: CaseSearchResult[]; results?: CaseSearchResult[] };
  return {
    results: envelope.data ?? envelope.results ?? [],
    query,
  };
}
